import { Link, useNavigate } from "react-router-dom";
import {
  Container, Row, Card, Breadcrumb, Col
} from "react-bootstrap";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Page as Paging,
  PageSettingsModel,
  Filter,
  Resize,
  FilterSettingsModel,
  TextWrapSettingsModel
} from "@syncfusion/ej2-react-grids";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider
} from "@whitespace/whitespace-ui";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { useEffect, useState, useCallback } from "react";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { parseUsingParseInt } from "helpers/stringUtils";
import ICollectorViewModel from "interfaces/ICollectorViewModel";
import IGetCollectorsResponseModel from "interfaces/Response/IGetCollectorsResponseModel";
import EditTemplate from "./Templates/EditTemplate";
import MapViewTemplate from "./Templates/MapViewTemplate";
import { mapCollectorModelToViewModel } from "./DataMappers/ManageCollectorMapper";

import "styles/sf-datagrid.scss";

const pageSize: number = parseUsingParseInt(process.env.REACT_APP_PAGE_SIZE);
const pageSettings: PageSettingsModel = {
  pageSize,
  pageSizes: [5, 10, 20, 50, "All"]
};
const wrapSettings: TextWrapSettingsModel = {
  wrapMode: "Content"
};
const filterSettings: FilterSettingsModel = { type: "Excel" };
let grid: GridComponent | undefined;

const ManageCollectors = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [renderingComplete, setRenderingComplete] = useState(false);

  const {
    isSending, errors, data, statusCode, sendRequest
  } = useAmplifyREST();
  const navigate = useNavigate();

  const [collectors, setCollectors] = useState<ICollectorViewModel[] | undefined>(undefined);

  useEffect(() => {
    sendRequest("settings/collectors", "GET");
  }, [sendRequest]);

  useEffect(() => {
    if (data && data.collectors) {
      setCollectors((data as IGetCollectorsResponseModel).collectors.map(collector => mapCollectorModelToViewModel(collector)));
    }
  }, [data, setCollectors]);

  useEffect(() => {
    if (errors) setShowExceptions(true);
  }, [errors]);

  useEffect(() => {
    if (![0, 200].includes(statusCode)) {
      setRenderingComplete(true);
    }
  }, [statusCode]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const isProcessing = isSending || !renderingComplete;

  return (
    <Page>
      {showExceptions && errors && (
        <AmplifyError
          errors={errors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="collectors-management">
              Collectors Management
              <Spinner
                animation="grow"
                visible={isProcessing}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
          <div className="float-end">
            <IconButton
              data-testid="add-provider"
              iconType={IconButtonType.Add}
              variant="outline-primary btn-create"
              onClick={() => navigate(RoutePathsEnum.SettingsCollectorsProvidersAdd)}
            >
              Add Provider
            </IconButton>
            <IconButton
              data-testid="add-vehicle"
              iconType={IconButtonType.Add}
              variant="outline-primary btn-create"
              className="ms-2"
              onClick={() => navigate(RoutePathsEnum.SettingsCollectorsVehiclesAdd)}
            >
              Add Vehicle
            </IconButton>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={12} className="d-flex">
              <Breadcrumb
                className="left-margin-large no-wrap"
                data-testid="breadcrumb"
              >
                <Breadcrumb.Item
                  data-testid="breadcrumb-settings"
                  linkAs={Link}
                  linkProps={{ to: RoutePathsEnum.Settings }}
                  onClick={() => navigate(RoutePathsEnum.Settings)}
                >
                  Settings
                </Breadcrumb.Item>
                <Breadcrumb.Item active data-testid="breadcrumb-collectors">
                  Collectors Management
                </Breadcrumb.Item>
              </Breadcrumb>
            </Col>
          </Row>
          <Divider />
          <section className="pt-2">
            <Container fluid>
              <Row>
                <div className="control-pane">
                  <div className="control-section">
                    <GridComponent
                      ref={(g: GridComponent) => {
                        grid = g;
                      }}
                      id="collectorsGrid"
                      data-testid="collectorsGrid"
                      allowPaging
                      allowResizing
                      pageSettings={pageSettings}
                      allowSorting
                      allowFiltering
                      filterSettings={filterSettings}
                      allowSelection={false}
                      dataSource={collectors}
                      actionComplete={() => {
                        makeGridAACompliant(grid!, () => {
                          setRenderingComplete(true);
                        });
                      }}
                      loadingIndicator={{ indicatorType: "Shimmer" }}
                      type=""
                      textWrapSettings={wrapSettings}
                      allowTextWrap
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="id"
                          headerText="id"
                          visible={false}
                          isPrimaryKey
                        />
                        <ColumnDirective
                          field="name"
                          headerText="Collector"
                        />
                        <ColumnDirective
                          field="type"
                          headerText="Type"
                          hideAtMedia="(min-width:1200px)"
                        />
                        <ColumnDirective
                          field="statusDisplayValue"
                          headerText="Status"
                          hideAtMedia="(min-width:800px)"
                        />
                        <ColumnDirective
                          field="trackingDeviceName"
                          headerText="Tracking Device"
                          hideAtMedia="(min-width:1200px)"
                        />

                        <ColumnDirective
                          headerText="Map Icons"
                          width={90}
                          allowResizing={false}
                          template={MapViewTemplate}
                          allowFiltering={false}
                          allowSorting={false}
                          allowReordering={false}
                        />
                        <ColumnDirective
                          headerText=""
                          width={50}
                          allowResizing={false}
                          template={EditTemplate}
                          allowFiltering={false}
                          allowSorting={false}
                          allowReordering={false}
                        />
                      </ColumnsDirective>
                      <Inject services={[Filter, Paging, Sort, Resize]} />
                    </GridComponent>
                  </div>
                </div>
              </Row>
            </Container>
          </section>
        </Card.Body>
      </Card>
    </Page>
  );
};

export default ManageCollectors;
